import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CalendarCheck2, KeyRound, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [note, setNote] = useState("");
  const [code, setCode] = useState("");

  useEffect(() => {
    Promise.all([http.get("/appointments"), http.get("/properties")])
      .then(([a, p]) => { setAppts(a.data); setProperties(p.data); })
      .catch(() => {});
  }, []);

  const next = appts.find((a) => a.status === "scheduled");

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Badge variant="outline">Student portal</Badge>
      <h1 className="font-display text-3xl font-bold text-primary mt-2" data-testid="student-dashboard-title">Hey {user?.name?.split(" ")[0]} 👋</h1>
      <p className="text-muted-foreground text-sm mt-1">Your cleanings, access info, and quick requests.</p>

      <Card className="mt-6 border-border/60 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80"><CalendarCheck2 size={14} /> Next cleaning</div>
          {next ? (
            <>
              <div className="font-display text-3xl font-bold mt-2">{next.scheduled_date}</div>
              <div className="opacity-80 text-sm mt-1">{next.property?.name || "Your place"}</div>
            </>
          ) : (
            <div className="font-display text-xl font-semibold mt-2 opacity-80">No upcoming cleaning yet</div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 mt-6">
        <Card className="border-border/60"><CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-primary font-display font-semibold"><KeyRound size={16} /> Access info</div>
          <Input placeholder="Door / building code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="student-door-code" />
          <Button size="sm" variant="outline" onClick={() => toast.success("Saved! We'll share it with your cleaner.")} data-testid="student-save-access">Save access info</Button>
        </CardContent></Card>

        <Card className="border-border/60"><CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-primary font-display font-semibold"><MessageSquare size={16} /> Leave a note for the cleaner</div>
          <Textarea placeholder="e.g. please don't move my desk papers, my cat hides under the bed…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="student-note" />
          <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => { toast.success("Note saved — your cleaner will see it."); setNote(""); }} data-testid="student-save-note">Save note</Button>
        </CardContent></Card>

        <Card className="border-border/60"><CardContent className="p-5">
          <div className="font-display font-semibold text-primary mb-3">Upcoming visits</div>
          {appts.length === 0 ? <div className="text-sm text-muted-foreground">No cleanings yet — your parent will schedule one soon.</div> : (
            <ul className="space-y-2 text-sm">
              {appts.slice(0, 5).map((a) => (
                <li key={a.id} className="flex justify-between border-b border-border/40 py-2 last:border-0">
                  <span>{a.scheduled_date}</span>
                  <Badge variant="outline">{a.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
}
